import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ExpandablePanel from '../../components/ExpandablePanel/ExpandablePanel';
import FilterChips from '../../components/FilterChips/FilterChips';
import ClientsCreateModal from './ClientsCreateModal';
import './client.css';
import DatePickerEs from '../../components/DatePickerEs/DatePickerEs';
import useGetClientsPaged from '../../hooks/client/useGetClient';
import { exportClientsExcel } from '../../services/clientService';
import { getUserById } from 'services/usersServices';
import { getAllBranch } from 'services/Configuration/CompanyService';
import normalizeRegistrationDate from 'helpers/normalizeRegistrationDate';
import { useSearchParams } from 'react-router-dom';
import { persistStateToUrl } from 'utils/urlState';
import { parseLocalDate } from 'utils/formatDate';
import { useUsersByRole } from 'hooks/user/useUsersByRole';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { clientsI18n } from './i18n';
import usePermissionsCrm from 'permissions/modules/crm/usePermissionsCrm';

export const userSacRol = 5;

const normalizeClientState = (value) => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  if (normalized === 'active' || normalized === 'activo') return 'active';
  if (normalized === 'inactive' || normalized === 'inactivo') return 'inactive';
  if (normalized === 'prospect' || normalized === 'prospecto')
    return 'prospect';

  return 'active';
};

const Clients = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const clientsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: clientsI18n });

  const { canCreateClient, canDownloadExcel, canViewClientDetailSection } =
    usePermissionsCrm();

  const { Card, Button, Input, Dropdown, IconButton, Table, Badge, ComboBox } =
    DesignSystemComponents;

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchClient, setSearchClient] = useState('');
  const [searchClientName, setSearchClientName] = useState('');
  const [searchClientRut, setSearchClientRut] = useState('');
  const [searchClientPhone, setSearchClientPhone] = useState('');
  const [searchClientEmail, setSearchClientEmail] = useState('');
  const [filterByExecutive, setFilterByExecutive] = useState(null);
  const [filterByOffice, setFilterByOffice] = useState(null);
  const [filterByState, setFilterByState] = useState(null);
  const [filterByDate, setFilterByDate] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [clientTablePage, setClientTablePage] = useState(0);
  const [pageSizeTable, setPageSizeTable] = useState(10);
  const [showCreate, setShowCreate] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [exportingExcel, setExportingExcel] = useState(false);
  const [filterByCategory, setFilterByCategory] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [sacExecutiveOptions, setSacExecutiveOptions] = useState([]);
  const [sacSearchText, setSacSearchText] = useState('');
  const [officeOptions, setOfficeOptions] = useState([]);

  const { fetchUsersByRole, isLoading: loadingSacExecutives } =
    useUsersByRole();
  const [officeSearchText, setOfficeSearchText] = useState('');
  const [urlHydrated, setUrlHydrated] = useState(false);
  const [stateClient, setStateClient] = useState(null);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'client':
        return 'name';
      case 'phone':
        return 'telephone';
      case 'email':
        return 'email';
      case 'sac':
        return 'sac';
      case 'office':
        return 'branchname';
      case 'state':
        return 'active';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setClientTablePage(0);
  };

  const assignCategory = (client) => {
    const key = String(client?.id ?? client?.rut ?? client?.client ?? '');
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    const mod = hash % 6; // 0–5
    if (mod < 3) return 'Masivo'; // 3 parts
    if (mod < 5) return 'Cartera'; // 2 parts
    return 'Alto Valor'; // 1 part
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'updateAt';
  const backendSortDesc =
    sortDirection === null ? true : sortDirection === 'desc';

  const { clients, clientsCount, loading, searchTooShort } = useGetClientsPaged(
    clientTablePage,
    pageSizeTable,
    searchClient,
    appliedFilters,
    backendSortKey,
    backendSortDesc
  );

  const clientsWithCategory = (clients || []).map((c) => ({
    ...c,
    categoria: assignCategory(c),
  }));

  const filteredClients = filterByCategory
    ? clientsWithCategory.filter((c) => c.categoria === filterByCategory.value)
    : clientsWithCategory;

  const onPageSizeChange = (newSize) => {
    setPageSizeTable(newSize);
    setClientTablePage(0);
  };

  const stateClientOptions = useMemo(
    () => [
      {
        label: t('clients_state_active'),
        value: 'active',
      },
      {
        label: t('clients_state_inactive'),
        value: 'inactive',
      },
    ],
    [t]
  );

  const advancedStateClientOptions = useMemo(
    () => [
      {
        label: t('clients_state_active'),
        value: 'active',
      },
      {
        label: t('clients_state_inactive'),
        value: 'inactive',
      },
      {
        label: t('clients_state_prospect'),
        value: 'prospect',
      },
    ],
    [t]
  );

  const categoryOptions = useMemo(
    () => [
      { label: t('clients_category_massive'), value: 'Masivo' },
      { label: t('clients_category_portfolio'), value: 'Cartera' },
      { label: t('clients_category_highValue'), value: 'Alto Valor' },
    ],
    [t]
  );

  const stateLabelMap = useMemo(
    () => ({
      active: t('clients_state_active'),
      inactive: t('clients_state_inactive'),
      prospect: t('clients_state_prospect'),
    }),
    [t]
  );

  useEffect(() => {
    setStateClient((prev) => {
      const next =
        stateClientOptions.find((option) => option.value === prev?.value) ||
        stateClientOptions[0];
      return next || null;
    });
  }, [stateClientOptions]);

  const formatRegistrationDate = (value) => {
    if (!value) return '';

    if (typeof value === 'string') return value;

    if (typeof value === 'object') {
      const { startDate, endDate } = value;

      const formatPart = (part) => {
        if (!part) return '';
        if (typeof part === 'string') return part;
        if (part instanceof Date && !Number.isNaN(part.getTime())) {
          return part.toLocaleDateString(clientsLanguage);
        }
        return String(part);
      };

      const startText = formatPart(startDate);
      const endText = formatPart(endDate);

      if (startText && endText && startText !== endText) {
        return `${startText} - ${endText}`;
      }
      return startText || endText || '';
    }

    return String(value);
  };

  const buildFilterChips = () => {
    if (!appliedFilters) return [];
    const chips = [];

    if (appliedFilters.name) {
      chips.push({
        id: 'name',
        label: t('clients_chip_name'),
        value: appliedFilters.name,
      });
    }

    if (appliedFilters.rut) {
      chips.push({
        id: 'rut',
        label: t('clients_chip_rut'),
        value: appliedFilters.rut,
      });
    }

    if (appliedFilters.phone) {
      chips.push({
        id: 'phone',
        label: t('clients_chip_phone'),
        value: appliedFilters.phone,
      });
    }

    if (appliedFilters.email) {
      chips.push({
        id: 'email',
        label: t('clients_chip_email'),
        value: appliedFilters.email,
      });
    }

    if (appliedFilters.sacId) {
      chips.push({
        id: 'sacId',
        label: t('clients_chip_sacExecutive'),
        value: filterByExecutive?.label || appliedFilters.sacId,
      });
    }

    if (appliedFilters.officeId) {
      chips.push({
        id: 'officeId',
        label: t('clients_chip_branch'),
        value: filterByOffice?.label || appliedFilters.officeId,
      });
    }

    const normalizedState = normalizeClientState(appliedFilters.state);
    if (appliedFilters.state && normalizedState !== 'active') {
      chips.push({
        id: 'state',
        label: t('clients_chip_state'),
        value: stateLabelMap[normalizedState] || appliedFilters.state,
      });
    }

    if (appliedFilters.registrationDate) {
      chips.push({
        id: 'registrationDate',
        label: t('clients_chip_registrationDate'),
        value: formatRegistrationDate(appliedFilters.registrationDate),
      });
    }

    if (filterByCategory?.value) {
      chips.push({
        id: 'category',
        label: t('clients_chip_category'),
        value: filterByCategory.label,
      });
    }

    return chips;
  };

  const handleRemoveFilterChip = (id) => {
    setAppliedFilters((prev) => {
      const next = { ...prev };

      switch (id) {
        case 'name': {
          delete next.name;
          setSearchClientName('');
          break;
        }
        case 'rut': {
          delete next.rut;
          setSearchClientRut('');
          break;
        }
        case 'phone': {
          delete next.phone;
          setSearchClientPhone('');
          break;
        }
        case 'email': {
          delete next.email;
          setSearchClientEmail('');
          break;
        }
        case 'sacId': {
          delete next.sacId;
          setFilterByExecutive(null);
          break;
        }
        case 'officeId': {
          delete next.officeId;
          setFilterByOffice(null);
          break;
        }
        case 'state': {
          next.state = 'active';
          setStateClient(
            advancedStateClientOptions.find(
              (option) => option.value === 'active'
            ) || stateClientOptions[0]
          );
          setFilterByState(null);
          break;
        }
        case 'registrationDate': {
          delete next.registrationDate;
          setFilterByDate(null);
          break;
        }
        case 'category': {
          setFilterByCategory(null);
          break;
        }
        default:
          break;
      }

      return next;
    });

    setClientTablePage(0);
  };

  const handleClearAllFilterChips = () => {
    handleClearFilters();
  };

  useEffect(() => {
    if (!urlHydrated) return;

    const q = searchParams.get('q');
    if (q !== null) {
      setSearchClient(q);
    }
  }, [urlHydrated]);

  useEffect(() => {
    if (!urlHydrated) return;

    setAppliedFilters((prev) => {
      const next = { ...(prev || {}) };

      if (searchClient?.trim()) {
        next.q = searchClient.trim();
      } else {
        delete next.q;
      }

      if (stateClient?.value) {
        next.state = stateClient.value;
      }

      return next;
    });

    setClientTablePage(0);
  }, [searchClient, stateClient, urlHydrated]);

  useEffect(() => {
    if (!urlHydrated || !appliedFilters) return;

    setSearchClientName(appliedFilters.name ?? '');
    setSearchClientRut(appliedFilters.rut ?? '');
    setSearchClientPhone(appliedFilters.phone ?? '');
    setSearchClientEmail(appliedFilters.email ?? '');
  }, [urlHydrated, appliedFilters]);

  useEffect(() => {
    if (!urlHydrated) return;

    persistStateToUrl({
      filters: appliedFilters,
      page: clientTablePage,
      pageSize: pageSizeTable,
      sortKey,
      sortDirection,
      setSearchParams,
    });
  }, [
    appliedFilters,
    clientTablePage,
    pageSizeTable,
    sortKey,
    sortDirection,
    urlHydrated,
  ]);

  useEffect(() => {
    if (!urlHydrated) return;
    if (!appliedFilters.state) return;

    const normalizedState = normalizeClientState(appliedFilters.state);
    const option = advancedStateClientOptions.find(
      (o) => o.value === normalizedState
    );

    if (option) {
      setFilterByState(option);
    }
  }, [advancedStateClientOptions, appliedFilters.state, urlHydrated]);

  useEffect(() => {
    if (!urlHydrated) return;
    if (!appliedFilters.category) return;

    const option = categoryOptions.find(
      (o) => o.value === appliedFilters.category
    );

    if (option) {
      setFilterByCategory(option);
    }
  }, [appliedFilters.category, categoryOptions, urlHydrated]);

  useEffect(() => {
    if (!urlHydrated) return;

    const { startDate, endDate } = appliedFilters;

    if (!startDate && !endDate) return;

    setFilterByDate({
      startDate: startDate ? parseLocalDate(startDate) : null,
      endDate: endDate ? parseLocalDate(endDate) : null,
    });
  }, [appliedFilters, urlHydrated]);

  useEffect(() => {
    if (!urlHydrated) return;
    if (!appliedFilters?.sacId) return;

    fetchSacExecutiveById(appliedFilters.sacId);
  }, [urlHydrated, appliedFilters?.sacId]);

  useEffect(() => {
    if (!urlHydrated) return;
    if (!appliedFilters?.officeId) return;
    if (!officeOptions.length) return;

    const option = officeOptions.find(
      (o) => String(o.value) === String(appliedFilters.officeId)
    );

    if (option) {
      setFilterByOffice(option);
    }
  }, [appliedFilters?.officeId, officeOptions, urlHydrated]);

  useEffect(() => {
    clientsI18n.changeLanguage(clientsLanguage);
  }, [clientsLanguage]);

  useEffect(() => {
    const filtersFromUrl = {};

    // filtros avanzados
    const name = searchParams.get('name');
    const rut = searchParams.get('rut');
    const phone = searchParams.get('phone');
    const email = searchParams.get('email');
    const sacId = searchParams.get('sacId');
    const officeId = searchParams.get('officeId');
    const category = searchParams.get('category');
    const state = searchParams.get('state');
    const q = searchParams.get('q');
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    if (name) filtersFromUrl.name = name;
    if (rut) filtersFromUrl.rut = rut;
    if (phone) filtersFromUrl.phone = phone;
    if (email) filtersFromUrl.email = email;
    if (sacId) filtersFromUrl.sacId = sacId;
    if (officeId) filtersFromUrl.officeId = officeId;
    if (category) filtersFromUrl.category = category;
    if (q) filtersFromUrl.q = q;

    filtersFromUrl.state = normalizeClientState(state ?? 'active');

    if (startDate || endDate) {
      filtersFromUrl.startDate = startDate;
      filtersFromUrl.endDate = endDate;
    }

    setAppliedFilters(filtersFromUrl);

    setClientTablePage(Number(searchParams.get('page') ?? 0));
    setPageSizeTable(Number(searchParams.get('pageSize') ?? 10));

    setUrlHydrated(true);
  }, []);

  const fetchSacExecutives = async (searchText = '') => {
    try {
      const users = await fetchUsersByRole(userSacRol, searchText);

      setSacExecutiveOptions(
        (users || []).map((u) => ({
          label: u.name,
          value: u.id,
        }))
      );
    } catch (error) {
      console.error('Error cargando ejecutivos SAC:', error);
      setSacExecutiveOptions([]);
    }
  };

  const fetchSacExecutiveById = async (id) => {
    try {
      const resp = await getUserById(id);

      const user = resp;

      if (!user) return;

      const option = {
        label: user.name,
        value: user.id,
      };

      setSacExecutiveOptions([option]);
      setFilterByExecutive(option);
    } catch (e) {
      console.error('Error cargando SAC por id', e);
    }
  };

  const handleExecutiveChange = (selected) => {
    setFilterByExecutive(selected);
    if (!selected) {
      setSacSearchText('');
      fetchSacExecutives();
    }
  };

  useEffect(() => {
    fetchSacExecutives();
  }, []);

  useEffect(() => {
    if (!sacSearchText || sacSearchText.length < 2) {
      return;
    }

    const timer = setTimeout(() => {
      fetchSacExecutives(sacSearchText);
    }, 400);

    return () => clearTimeout(timer);
  }, [sacSearchText]);

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const resp = await getAllBranch();
        setOfficeOptions(
          (resp || []).map((o) => ({
            label: o.name,
            value: o.id,
          }))
        );
      } catch (e) {
        console.error('Error cargando sucursales', e);
        setOfficeOptions([]);
      }
    };

    loadOffices();
  }, []);

  const filteredOfficeOptions = useMemo(() => {
    if (!officeSearchText) return officeOptions;

    const text = officeSearchText.toLowerCase();
    return officeOptions.filter((o) => o.label.toLowerCase().includes(text));
  }, [officeOptions, officeSearchText]);

  const handleApplyFilters = () => {
    const newFilters = {};

    if (searchClient?.trim()) newFilters.q = searchClient.trim();
    if (searchClientName?.trim()) newFilters.name = searchClientName.trim();
    if (searchClientRut?.trim()) newFilters.rut = searchClientRut.trim();
    if (searchClientPhone?.trim()) newFilters.phone = searchClientPhone.trim();
    if (searchClientEmail?.trim()) newFilters.email = searchClientEmail.trim();

    if (filterByExecutive?.value) newFilters.sacId = filterByExecutive.value;
    if (filterByOffice?.value) newFilters.officeId = filterByOffice.value;

    newFilters.state = normalizeClientState(
      filterByState?.value ?? stateClient?.value ?? 'active'
    );

    if (filterByCategory?.value) {
      newFilters.category = filterByCategory.value;
    }

    if (filterByDate) {
      const normalized = normalizeRegistrationDate(filterByDate);
      if (normalized) {
        newFilters.startDate = normalized.dateFrom;
        newFilters.endDate = normalized.dateTo;
      }
    }

    setAppliedFilters(newFilters);
    setClientTablePage(0);
    setShowPanel(false);
  };

  const handleClearFilters = () => {
    setSearchClientName('');
    setSearchClientRut('');
    setSearchClientPhone('');
    setSearchClientEmail('');
    setFilterByExecutive(null);
    setFilterByOffice(null);
    setFilterByState(null);
    setFilterByDate(null);
    setFilterByCategory(null);
    setStateClient(stateClientOptions[0]);
    setAppliedFilters({ state: 'active' });
    setClientTablePage(0);
    setSacSearchText('');
    fetchSacExecutives();
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      await exportClientsExcel(appliedFilters);
    } catch (error) {
      console.error('Error descargando Excel:', error);
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <div>
      {/* HEADER */}
      <div className='mb-4 d-flex justify-content-between'>
        <div>
          <h2 className=' fw-semibold'>{t('clients_title')}</h2>
          <h4 className=' fw-normal'>{t('clients_subtitle')}</h4>
        </div>
        <Button
          label={t('clients_newClient')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => setShowCreate(true)}
          disabled={!canCreateClient}
        />
      </div>
      <Card specialPadding='p-4'>
        <div className='row'>
          <div className='col-7'>
            <Input
              icon='search'
              name='searchClient'
              onChange={(e) => setSearchClient(e.target.value)}
              placeholder={t('clients_searchPlaceholder')}
              type='text'
              value={searchClient}
            />
          </div>
          <div className='col-3'>
            <div className='d-flex'>
              <div className='flex-grow-1 me-4'>
                <Dropdown
                  placeholder={t('clients_select')}
                  defaultValue={stateClientOptions[0]}
                  onChange={(selected) => setStateClient(selected)}
                  options={stateClientOptions}
                  value={stateClient}
                />
              </div>
              <IconButton
                ariaLabel={t('clients_filterAriaLabel')}
                icon='funnel'
                onClick={() => setShowPanel((prev) => !prev)}
                className='mt-1'
                size='lg'
              />
            </div>
          </div>
          <div className='col-2'>
            <div className='d-flex justify-content-end'>
              <Button
                iconStart='download'
                label={t('clients_downloadExcel')}
                variant='naked'
                className='mt-2'
                size='sm'
                onClick={handleExportExcel}
                disabled={!canDownloadExcel || exportingExcel}
              />
            </div>
          </div>
        </div>
        {!showPanel && (
          <FilterChips
            filters={buildFilterChips()}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilterChips}
          />
        )}
        {showPanel && (
          <ExpandablePanel
            open={showPanel}
            showActions
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onClose={() => setShowPanel(false)}
          >
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div>
                <h4 className='fw-semibold '>{t('clients_advancedFilters')}</h4>
                <p className='mini font-light fw-normal'>
                  {t('clients_advancedFiltersSubtitle')}
                </p>
              </div>
              <IconButton
                ariaLabel={t('clients_closePanelAriaLabel')}
                icon='close'
                size='md'
                onClick={() => setShowPanel(false)}
              />
            </div>
            <div className='row'>
              <div className='col-3'>
                <Input
                  label={t('clients_nameLabel')}
                  name='clientName'
                  onChange={(e) => setSearchClientName(e.target.value)}
                  placeholder={t('clients_namePlaceholder')}
                  type='text'
                  value={searchClientName}
                />
              </div>
              <div className='col-3'>
                <Input
                  label={t('clients_rutLabel')}
                  name='clientRut'
                  onChange={(e) => setSearchClientRut(e.target.value)}
                  placeholder={t('clients_rutPlaceholder')}
                  type='text'
                  value={searchClientRut}
                />
              </div>
              <div className='col-3'>
                <Input
                  label={t('clients_phoneLabel')}
                  name='clientPhone'
                  onChange={(e) => setSearchClientPhone(e.target.value)}
                  placeholder={t('clients_phonePlaceholder')}
                  type='text'
                  value={searchClientPhone}
                />
              </div>
              <div className='col-3'>
                <Input
                  label={t('clients_emailLabel')}
                  name='clientEmail'
                  onChange={(e) => setSearchClientEmail(e.target.value)}
                  placeholder={t('clients_emailPlaceholder')}
                  type='email'
                  value={searchClientEmail}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('clients_sacExecutiveLabel')}
                    placeholder={t('clients_sacExecutivePlaceholder')}
                    noResultText={t('clients_noResults')}
                    value={filterByExecutive}
                    options={sacExecutiveOptions}
                    isLoading={loadingSacExecutives}
                    onChange={handleExecutiveChange}
                    onInputChange={(text) => setSacSearchText(text)}
                  />
                </div>
              </div>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('clients_branchLabel')}
                    placeholder={t('clients_branchPlaceholder')}
                    noResultText={t('clients_noResults')}
                    value={filterByOffice}
                    options={filteredOfficeOptions}
                    onChange={(selected) => setFilterByOffice(selected)}
                    onInputChange={(text) => setOfficeSearchText(text)}
                  />
                </div>
              </div>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <p className='caption mb-1'>
                    {t('clients_registrationDateLabel')}
                  </p>
                  <DatePickerEs
                    initialRange={filterByDate}
                    value={filterByDate}
                    onChange={setFilterByDate}
                  />
                </div>
              </div>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('clients_stateLabel')}
                    noResultText={t('clients_noResults')}
                    placeholder={t('clients_statePlaceholder')}
                    onChange={(selected) => setFilterByState(selected)}
                    value={filterByState}
                    options={advancedStateClientOptions}
                  />
                </div>
              </div>
            </div>
            <div className='row pb-3'>
              <div className='col-3'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('clients_categoryLabel')}
                    noResultText={t('clients_noResults')}
                    placeholder={t('clients_categoryPlaceholder')}
                    onChange={(selected) => setFilterByCategory(selected)}
                    value={filterByCategory}
                    options={categoryOptions}
                  />
                </div>
              </div>
            </div>
          </ExpandablePanel>
        )}
        {searchTooShort && (
          <p className='caption color-neutral-600 mb-2'>
            {t('clients_search_min_chars')}
          </p>
        )}
        <div className='table-container table-client'>
          <Table
            columns={[
              {
                accessor: 'client',
                label: t('clients_table_client'),
                sort: true,
                render: (info) => (
                  <div className='client-double'>
                    <p className='caption fw-normal ellipsis'>{info.client}</p>
                    <p className='mini fw-normal '>{info.rut}</p>
                  </div>
                ),
              },
              {
                accessor: 'phone',
                label: t('clients_table_phone'),
                sort: true,
              },
              {
                accessor: 'email',
                label: t('clients_table_email'),
                sort: true,
              },
              {
                accessor: 'sac',
                label: t('clients_table_sac'),
                sort: true,
              },
              {
                accessor: 'office',
                label: t('clients_table_branch'),
                sort: true,
              },
              {
                accessor: 'category',
                label: t('clients_table_category'),
                render: (info) => (
                  <Badge
                    text={
                      categoryOptions.find(
                        (option) => option.value === info.category
                      )?.label || info.category
                    }
                    color={
                      info.category === 'Masivo'
                        ? 'neutral'
                        : info.category === 'Cartera'
                          ? 'light'
                          : 'primary'
                    }
                    size='sm'
                  />
                ),
              },
              {
                accessor: 'state',
                label: t('clients_table_state'),
                sort: true,
                render: (info) => {
                  const normalizedState = normalizeClientState(info.state);
                  return (
                    <Badge
                      text={stateLabelMap[normalizedState] || info.state}
                      color={normalizedState === 'active' ? 'success' : 'light'}
                      size='sm'
                    />
                  );
                },
              },
              {
                label: t('clients_table_actions'),
                render: (info) => (
                  <div className='d-flex justify-content-center'>
                    {canViewClientDetailSection ? (
                      <Link
                        to={`/crm/detalles-cliente/${info.id}`}
                        className='text-decoration-none'
                      >
                        <IconButton
                          ariaLabel={t('clients_viewAriaLabel')}
                          icon='eye'
                          size='md'
                        />
                      </Link>
                    ) : (
                      <IconButton
                        ariaLabel={t('clients_viewAriaLabel')}
                        icon='eye'
                        size='md'
                        disabled
                      />
                    )}
                  </div>
                ),
              },
            ]}
            data={clients}
            onPageChange={setClientTablePage}
            page={clientTablePage}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            loading={loading}
            paginationMode='server'
            pageSize={pageSizeTable}
            totalItems={clientsCount}
            onPageSizeChange={onPageSizeChange}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
          />
        </div>
        <ClientsCreateModal
          DesignSystemComponents={DesignSystemComponents}
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSave={() => {
            setShowCreate(false);
            // Refetch de clientes sucederá automáticamente por el cambio en dependencias
            setClientTablePage(0);
          }}
        />
      </Card>
    </div>
  );
};
export default Clients;
